import Axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { env } from '@/config/env';
import { GlytoucanApiResponseSchema } from '@/types/api';
import { ErrorType, GlyTouCanError } from '@/types/error';
import { getBaseURL } from '@/utils/phase';
import { validate } from '@/utils/validation';

const authRequestInterceptor = (config: InternalAxiosRequestConfig) => {
  if (config.headers) {
    config.headers.Accept = 'application/json';
  }
  // NOTE: Accessing the Web APIs from different origins on the web browser may result in errors
  //       related to CORS (Cross-Origin Resource Sharing).
  //       To avoid this kind of errors, the server-side Web APIs need to return a response
  //       with the appropriate `Access-Control-Allow-Origin` header as part of the HTTP request header.
  //       In principle, a Web API can be accessed from any origin if `access-control-allow-origin: *` is set.
  //       However, the XHR and `fetch()` function still raise CORS errors even for responses set to
  //       `access-control-allow-origin: *` if the credential is enabled.
  //       The credentials are necessary for the web browser to properly retrieve cookies and other information returned by the APIs.
  //       Since GlyComb's public Web APIs are designed to return a response with `access-control-allow-origin: *` set,
  //       and the user interface needs to retrieve a cookie representing authentication information from the response
  //       and store it in the web browser, the `withCredentials` property must be set to `true` here.
  //       This means that any access to GlyComb's Web APIs from an origin different from GlyComb's origin (e.g., localhost:3000)
  //       will result in a CORS error, so it is strongly recommended to utilize mock APIs in a local development environment.
  //       <https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Headers/Access-Control-Allow-Origin>
  //       <https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/withCredentials>
  //       <https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#including_credentials>
  config.withCredentials = true;
  return config;
};

export const api = Axios.create({
  baseURL: env.ENABLE_API_MOCKING ? env.API_URL : getBaseURL(),
  // NOTE: Use the fetch adapter to ignore redirects by APIs that return status code 302 (enable `redirect: 'manual'`).
  //       <https://github.com/axios/axios/issues/3924>
  adapter: 'fetch',
});

api.interceptors.request.use(authRequestInterceptor);
api.interceptors.response.use(
  response => {
    // Skip validation for logout redirect
    // if (response.config.url === GlyTouCan_API.LOGOUT && response.status === 0) {
    //   return response;
    // }

    if (typeof response.data === 'string') {
      return response;
    }

    const data = validate(
      response.data,
      GlytoucanApiResponseSchema,
      response.request?.responseURL,
    );
    if (data.errcode && data.errcode !== '') {
      return Promise.reject(
        new GlyTouCanError({
          message: data.errcode,
          errorType: 'INVALID_INPUT_ERROR',
          name: 'InvalidInputError',
          url: response.request?.responseURL,
        }),
      );
    }
    return response;
  },
  error => {
    // Here, we will show some notification messages, process redirects, etc...

    if (error instanceof AxiosError) {
      const statusCode = error.status;
      const [errorType, name]: [ErrorType, string] =
        statusCode === 401
          ? ['AUTHORIZATION_ERROR', 'AuthorizationError']
          : statusCode
            ? ['HTTP_ERROR', 'HttpError']
            : ['REQUEST_UNREACHABLE_ERROR', 'RequestUnreachableError'];

      const url = error.request?.responseURL ?? '';

      return Promise.reject(
        new GlyTouCanError({
          message: error.message,
          errorType: errorType,
          name: name,
          cause: error.cause,
          stack: error.stack,
          url:
            url !== '' ? url : `${error.config?.baseURL}${error.config?.url}`,
        }),
      );
    }

    return Promise.reject(error);
  },
);
